import { api } from "./api";
import { API_BASE } from "./apiBase";

/**
 * OAuth for providers that must run in the system browser (Google forbids
 * webview login). Opens the flow in SFSafariViewController and completes when
 * the server's callback deep-links back with a one-time handoff token. The
 * token is bound to a per-attempt nonce, so an intercepted link is useless.
 * Deep link arrives as a Universal Link (https, build >= 14) or the legacy
 * caloly:// scheme (older builds via the /auth/native interstitial).
 */
export async function nativeBrowserOAuth(provider: "google" | "apple"): Promise<void> {
  const { Browser } = await import("@capacitor/browser");
  const { App } = await import("@capacitor/app");

  const nonce = crypto.randomUUID();

  const done = new Promise<void>((resolve, reject) => {
    let settled = false;
    let redeeming = false; // deep link received — a closing browser sheet is no longer a cancel
    const finish = (fn: () => void) => { if (!settled) { settled = true; cleanup(); fn(); } };
    let sub: { remove: () => void } | null = null;
    let cancelSub: { remove: () => void } | null = null;
    const cleanup = () => { sub?.remove(); cancelSub?.remove(); };

    const extractToken = (url: string): string | null => {
      if (url.startsWith("caloly://auth")) {
        return new URL(url.replace("caloly://", "https://caloly.com/")).searchParams.get("token");
      }
      if (url.includes("/auth/native")) {
        try { return new URL(url).searchParams.get("token"); } catch { return null; }
      }
      return null;
    };

    App.addListener("appUrlOpen", async ({ url }) => {
      const token = extractToken(url);
      if (!token) return;
      redeeming = true;
      try {
        await api.redeemHandoff(token, nonce);
        await Browser.close().catch(() => { /* already closed */ });
        finish(resolve);
      } catch (e) {
        finish(() => reject(e));
      }
    }).then((s) => { sub = s; });

    // The user swiped the sheet away — but iOS also fires this when the deep
    // link closes the browser, so give a redemption in flight time to win.
    Browser.addListener("browserFinished", () => {
      setTimeout(() => {
        if (!redeeming) finish(() => reject(new Error("cancelled")));
      }, 700);
    }).then((s) => { cancelSub = s; });
  });

  await Browser.open({
    url: `${API_BASE || "https://caloly.com"}/api/auth/oauth/${provider}/start?native=1&nonce=${nonce}`,
  });
  await done;
}
